import React from 'react';
import { motion } from 'motion/react';
import { 
  DollarSign, 
  Wrench, 
  Globe, 
  Cpu, 
  ShieldCheck, 
  CheckCircle2, 
  Sparkles, 
  ArrowRight, 
  Server, 
  Network, 
  CreditCard, 
  Lock, 
  FileText, 
  MapPin, 
  Calculator,
  Monitor,
  Wifi,
  Building2,
  Clock,
  Layers,
  Check,
  CheckCircle
} from 'lucide-react';

export interface PricingPageProps {
  onNavigateToQuote: (initialProblem?: string, preferredMode?: 'onsite' | 'pickup' | 'remote') => void;
  onNavigateToWebOnboarding: () => void;
  onNavigateToContact: () => void;
  onNavigateToEstimator?: () => void;
  onViewTerms?: () => void;
}

interface LocationService {
  id: string;
  title: string;
  badge: string;
  rate: string;
  rateUnit: string;
  minBilling: string;
  description: string;
  criteriaLabel: string;
  criteriaValue: string;
  includedServices: string[];
  buttonLabel: string;
  requestType: string;
  preferredMode: 'onsite' | 'pickup' | 'remote';
  icon: React.ReactNode;
  badgeClass: string;
  borderClass: string;
  buttonClass: string;
  glowClass: string;
}

const LOCATION_SERVICES: LocationService[] = [
  {
    id: 'remote-support',
    title: 'Remote Support',
    badge: 'Screen Share',
    rate: '$37.50',
    rateUnit: '/ 30 min',
    minBilling: '30-min min ($37.50) • $37.50 / 30m thereafter',
    description: 'Fast remote screen-share assistance for software repairs, email/printer issues, and virus cleanups with zero travel required.',
    criteriaLabel: 'Availability',
    criteriaValue: 'Anywhere with internet access — zero travel fee required.',
    includedServices: [
      'PC, Mac, & laptop software diagnostics',
      'Software installs & configuration',
      'Virus, spyware, & malware removals',
      'Email client & network printer setup',
      'System tune-ups & speed optimization',
      'Software troubleshooting & driver updates',
      'DNS filtering & parental safety configuration',
      'Fast, secure screen share with zero travel fee'
    ],
    buttonLabel: 'Request Remote ($37.50)',
    requestType: 'Remote Support',
    preferredMode: 'remote',
    icon: <Wifi className="w-5 h-5 text-violet-400" />,
    badgeClass: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
    borderClass: 'border-violet-500/30 hover:border-violet-500/60',
    buttonClass: 'bg-violet-500 hover:bg-violet-400 text-neutral-950',
    glowClass: 'shadow-[0_0_30px_rgba(139,92,246,0.06)]'
  },
  {
    id: 'in-shop-bench',
    title: 'In-Shop / Bench',
    badge: 'Device Drop-Off',
    rate: '$75',
    rateUnit: '/ hour',
    minBilling: '1-hr min ($75) • $37.50 / 30m thereafter',
    description: 'Available to all customers who drop off their computer, laptop, or device directly to us for bench repair.',
    criteriaLabel: 'Eligibility',
    criteriaValue: 'Open to all clients who drop off hardware directly for bench diagnostics.',
    includedServices: [
      'PC, laptop, Mac, iPhone, & Android repairs',
      'Software installs & configuration',
      'Virus, spyware, & malware removals',
      'Hardware component diagnostics & SSD upgrades',
      'Operating system restorations & clean reinstalls',
      'Data backup, drive cloning & transfers',
      'DNS filtering & device protection setup',
      'Dedicated bench rate with zero travel charge'
    ],
    buttonLabel: 'Request In-Shop ($75)',
    requestType: 'In-Shop Bench',
    preferredMode: 'pickup',
    icon: <Cpu className="w-5 h-5 text-emerald-400" />,
    badgeClass: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    borderClass: 'border-emerald-500/30 hover:border-emerald-500/60',
    buttonClass: 'bg-emerald-500 hover:bg-emerald-400 text-neutral-950',
    glowClass: 'shadow-[0_0_30px_rgba(16,185,129,0.06)]'
  },
  {
    id: 'hometown-rate',
    title: 'Hometown Rate',
    badge: 'Licking & 10-Mile Radius',
    rate: '$75',
    rateUnit: '/ hour',
    minBilling: '1-hr min ($75) • $37.50 / 30m thereafter',
    description: 'Exclusive on-site rate for residential or small business clients physically located inside Licking city limits / 65542 or within a 10-mile radius.',
    criteriaLabel: 'Coverage Area',
    criteriaValue: 'Licking city limits / 65542 zip code & within a 10-mile radius.',
    includedServices: [
      'PC, laptop, Mac, iPhone, & Android repairs',
      'Software installs & configuration',
      'Virus, spyware, & malware removals',
      'Printer & scanner configuration',
      'Wi-Fi router & mesh network setup',
      'Ethernet wiring repairs',
      'DNS Website Filtering/Family Internet Controls',
      'Direct on-site visit within 10-mile radius with travel baked in'
    ],
    buttonLabel: 'Request Licking ($75)',
    requestType: 'Hometown Rate (Licking & 10-Mile Radius)',
    preferredMode: 'onsite',
    icon: <MapPin className="w-5 h-5 text-teal-400" />,
    badgeClass: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
    borderClass: 'border-teal-500/30 hover:border-teal-500/60',
    buttonClass: 'bg-teal-500 hover:bg-teal-400 text-neutral-950',
    glowClass: 'shadow-[0_0_30px_rgba(20,184,166,0.06)]'
  },
  {
    id: 'texas-county',
    title: 'Texas County',
    badge: 'County Route',
    rate: '$100',
    rateUnit: '/ hour',
    minBilling: '1-hr min ($100) • $50.00 / 30m thereafter',
    description: 'Direct on-site visit with travel time fully baked in across Texas County townships (excluding Licking).',
    criteriaLabel: 'Coverage Areas',
    criteriaValue: 'Houston, Cabool, Raymondville, Summersville, Plato, Success, & townships.',
    includedServices: [
      'PC, laptop, Mac, iPhone, & Android repairs',
      'Software installs & configuration',
      'Virus, spyware, & malware removals',
      'Printer & scanner configuration',
      'Wi-Fi router & mesh network setup',
      'Ethernet wiring repairs',
      'DNS Website Filtering/Family Internet Controls',
      'Direct on-site visit with travel baked in'
    ],
    buttonLabel: 'Request County ($100)',
    requestType: 'Texas County On-Site',
    preferredMode: 'onsite',
    icon: <Building2 className="w-5 h-5 text-cyan-400" />,
    badgeClass: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
    borderClass: 'border-cyan-500/30 hover:border-cyan-500/60',
    buttonClass: 'bg-cyan-500 hover:bg-cyan-400 text-neutral-950',
    glowClass: 'shadow-[0_0_30px_rgba(6,182,212,0.06)]'
  },
  {
    id: 'regional-route',
    title: 'Regional Route',
    badge: 'Regional Travel',
    rate: '$150',
    rateUnit: '/ hour',
    minBilling: '1-hr min ($150) • $75.00 / 30m thereafter',
    description: 'Direct on-site technical support adjusted for extended highway travel to neighboring regional towns.',
    criteriaLabel: 'Coverage Areas',
    criteriaValue: 'Mountain Grove, Salem, Willow Springs, Rolla, St. Robert, Waynesville, Edgar Springs.',
    includedServices: [
      'PC, laptop, Mac, iPhone, & Android repairs',
      'Software installs & configuration',
      'Virus, spyware, & malware removals',
      'Printer & scanner configuration',
      'Wi-Fi router & mesh network setup',
      'Ethernet wiring repairs',
      'DNS Website Filtering/Family Internet Controls',
      'Extended highway travel fully baked in'
    ],
    buttonLabel: 'Request Regional ($150)',
    requestType: 'Regional Route On-Site',
    preferredMode: 'onsite',
    icon: <Globe className="w-5 h-5 text-sky-400" />,
    badgeClass: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
    borderClass: 'border-sky-500/30 hover:border-sky-500/60',
    buttonClass: 'bg-sky-500 hover:bg-sky-400 text-neutral-950',
    glowClass: 'shadow-[0_0_30px_rgba(14,165,233,0.06)]'
  }
];

export const PricingPage: React.FC<PricingPageProps> = ({
  onNavigateToQuote,
  onNavigateToWebOnboarding,
  onNavigateToContact,
  onNavigateToEstimator,
  onViewTerms
}) => {
  return (
    <div className="space-y-12 sm:space-y-16 max-w-7xl mx-auto px-2 sm:px-4 py-2">
      {/* Top Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-mono font-bold uppercase tracking-wider border border-brand-500/20 select-none">
          <DollarSign className="w-3.5 h-3.5" />
          Direct &amp; Upfront Pricing
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-black text-white tracking-tight">
          Prices &amp; Service Rates
        </h1>
        <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
          Transparent, straightforward pricing with clear minimums and zero hidden fees. Explore our direct support options below or test typical times with our interactive Price Estimator.
        </p>
      </div>

      {/* Interactive Estimator Tool Banner */}
      {onNavigateToEstimator && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
          className="p-6 sm:p-7 rounded-3xl bg-gradient-to-r from-emerald-950/40 via-neutral-950 to-brand-950/40 border border-emerald-500/30 hover:border-emerald-500/50 transition-all text-left shadow-xl"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4 flex-1">
              <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-2xl shrink-0 border border-emerald-500/20 mt-0.5">
                <Calculator className="w-6 h-6" />
              </div>
              <div className="space-y-3 w-full">
                <div>
                  <div className="inline-flex items-center gap-2 py-0.5 px-2.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-mono font-bold uppercase tracking-wider border border-emerald-500/20 mb-1.5">
                    Interactive Estimator Tool
                  </div>
                  <h2 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                    Interactive Price &amp; Time Estimator
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mt-1">
                    Calculate expected job times and upfront price ranges for virus cleanups, SSD upgrades, Wi-Fi setups, server deployments, and custom websites.
                  </p>
                </div>

                {/* 4 Typical Time Benchmarks */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
                  <div className="p-2.5 rounded-xl bg-neutral-900/80 border border-neutral-800 text-center">
                    <span className="text-[10px] font-mono uppercase text-slate-400 block font-semibold">Virus Cleanup</span>
                    <span className="text-xs font-bold text-emerald-400 font-mono">2–3 hrs</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-neutral-900/80 border border-neutral-800 text-center">
                    <span className="text-[10px] font-mono uppercase text-slate-400 block font-semibold">SSD Upgrade</span>
                    <span className="text-xs font-bold text-emerald-400 font-mono">2–3 hrs</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-neutral-900/80 border border-neutral-800 text-center">
                    <span className="text-[10px] font-mono uppercase text-slate-400 block font-semibold">Wi-Fi Setup</span>
                    <span className="text-xs font-bold text-emerald-400 font-mono">1–1.5 hrs</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-neutral-900/80 border border-neutral-800 text-center">
                    <span className="text-[10px] font-mono uppercase text-slate-400 block font-semibold">Server Deploy</span>
                    <span className="text-xs font-bold text-emerald-400 font-mono">3–8 hrs</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={onNavigateToEstimator}
              className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-brand-500 hover:from-emerald-400 hover:to-brand-400 text-neutral-950 font-display font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 shrink-0 shadow-lg"
            >
              <span>Launch Price Estimator</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Services & Pricing Matrix */}
      <div className="space-y-6">
        <div className="text-left border-b border-neutral-900 pb-4 space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-brand-400">
            <Layers className="w-3.5 h-3.5" />
            Services &amp; Pricing Matrix
          </div>
          <h2 className="text-xl sm:text-2xl font-display font-black text-white tracking-tight">
            Direct &amp; Location-Based Support Services
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-4xl pt-0.5">
            Standard hourly rates with a 1-hour minimum for on-site/bench (or 30-min minimum for Remote Support), then billed in 30-minute increments thereafter (rounded up). Travel time is fully baked into all on-site rates with zero surprise mileage fees.
          </p>
        </div>

        {/* 5 Location Support Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left items-stretch">
          {LOCATION_SERVICES.map((srv) => (
            <div 
              key={srv.id} 
              className={`p-6 sm:p-7 rounded-3xl bg-neutral-950/90 border ${srv.borderClass} ${srv.glowClass} shadow-xl flex flex-col justify-between transition-all duration-300 relative group`}
            >
              <div className="space-y-4">
                {/* Header: Title, Badge & Icon */}
                <div className="flex items-start justify-between gap-2 border-b border-neutral-900 pb-3.5">
                  <div>
                    <h3 className="text-lg sm:text-xl font-display font-bold text-white group-hover:text-brand-300 transition-colors">
                      {srv.title}
                    </h3>
                    <span className={`inline-block mt-1 text-[11px] font-mono px-2.5 py-0.5 rounded-full border font-bold uppercase tracking-wider ${srv.badgeClass}`}>
                      {srv.badge}
                    </span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-neutral-900/80 border border-neutral-800 shrink-0">
                    {srv.icon}
                  </div>
                </div>

                {/* Price Display & Minimums */}
                <div className="bg-neutral-900/60 p-3.5 rounded-2xl border border-neutral-800/80 space-y-1">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-display font-black text-white">{srv.rate}</span>
                    <span className="text-slate-400 font-mono text-xs">{srv.rateUnit}</span>
                  </div>
                  <div className="text-[11px] font-mono text-brand-400 font-semibold flex items-center gap-1.5">
                    <Clock className="w-3 h-3 shrink-0" />
                    <span>{srv.minBilling}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-xs text-slate-300 leading-relaxed">
                  {srv.description}
                </p>

                {/* Availability / Eligibility / Coverage */}
                <div className="p-3 rounded-xl bg-neutral-900/40 border border-neutral-900 space-y-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold block">
                    {srv.criteriaLabel}:
                  </span>
                  <p className="text-xs text-slate-200 font-medium leading-relaxed">
                    {srv.criteriaValue}
                  </p>
                </div>

                {/* Included Services Checklist */}
                <div className="pt-2 border-t border-neutral-900 space-y-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold block">
                    Included Services:
                  </span>
                  <ul className="space-y-1.5 text-xs text-slate-300">
                    {srv.includedServices.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Action Button */}
              <button 
                onClick={() => onNavigateToQuote(srv.requestType, srv.preferredMode)} 
                className={`mt-6 w-full py-3 rounded-xl ${srv.buttonClass} text-xs font-bold font-mono uppercase tracking-wider transition-all duration-200 cursor-pointer shadow-md active:scale-95 flex items-center justify-center gap-2`}
              >
                <span>{srv.buttonLabel}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Specialized Capabilities & Web Development */}
      <div className="space-y-6">
        <div className="text-left border-b border-neutral-900 pb-4 space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-brand-400">
            <Sparkles className="w-3.5 h-3.5" />
            Specialized Capabilities &amp; Web Development
          </div>
          <h2 className="text-xl sm:text-2xl font-display font-black text-white tracking-tight">
            Infrastructure Engineering &amp; Custom Websites
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-4xl pt-0.5">
            Enterprise network systems, commercial cabling, camera installations, Starlink setups, and modern custom web projects.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-left items-stretch">
          {/* Network Engineering */}
          <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950/90 border border-amber-500/30 hover:border-amber-500/60 shadow-[0_0_35px_rgba(245,158,11,0.06)] flex flex-col justify-between transition-all duration-300 relative group">
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-2 border-b border-neutral-900 pb-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-display font-bold text-white group-hover:text-amber-300 transition-colors">
                    Network Engineering
                  </h3>
                  <span className="inline-block mt-1 text-[11px] font-mono px-3 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 font-bold uppercase tracking-wider">
                    Infrastructure
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 shrink-0">
                  <Network className="w-6 h-6" />
                </div>
              </div>

              <div className="bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800/80 space-y-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl font-display font-black text-white">$200</span>
                  <span className="text-slate-400 font-mono text-sm">/ hour</span>
                </div>
                <div className="text-[11px] font-mono text-amber-400 font-semibold flex items-center gap-1.5">
                  <Clock className="w-3 h-3 shrink-0" />
                  <span>1-hr min ($200) • $100.00 / 30 min thereafter, rounded up</span>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Network engineering, server setups, Starlink installs, camera installations, and structured data cabling for businesses.
              </p>

              <div className="p-3 rounded-xl bg-neutral-900/40 border border-neutral-900 space-y-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold block">
                  Target Systems:
                </span>
                <p className="text-xs text-slate-200 font-medium leading-relaxed">
                  Commercial servers, VLANs, rack wiring, Active Directory, NVR security cameras &amp; enterprise setups.
                </p>
              </div>

              <div className="pt-3 border-t border-neutral-900 space-y-2.5">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold block">
                  Included Services:
                </span>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Complex network builds &amp; VLAN / WAN engineering</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Windows &amp; Linux server deployment and rebuilds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Enterprise software installs &amp; server configuration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Structured data cabling (CAT5e/6/7) &amp; rack dressing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Firewall configuration &amp; security hardening</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Starlink installs &amp; network setup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Camera installations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                    <span>Active Directory, Azure / Entra ID, &amp; Workspace</span>
                  </li>
                </ul>
              </div>
            </div>

            <button 
              onClick={() => onNavigateToQuote('Network Engineering', 'onsite')} 
              className="mt-6 w-full py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider transition-all duration-200 cursor-pointer shadow-md active:scale-95 flex items-center justify-center gap-2"
            >
              <span>Request Engineering ($200)</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Website Builds */}
          <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950/90 border border-brand-500/40 hover:border-brand-500/70 shadow-[0_0_35px_rgba(245,158,11,0.08)] flex flex-col justify-between transition-all duration-300 relative group">
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-2 border-b border-neutral-900 pb-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-display font-bold text-white group-hover:text-brand-300 transition-colors">
                    Website Builds
                  </h3>
                  <span className="inline-block mt-1 text-[11px] font-mono px-3 py-0.5 rounded-full bg-brand-500/20 text-brand-300 border border-brand-500/40 font-bold uppercase tracking-wider">
                    Custom Web
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20 shrink-0">
                  <Globe className="w-6 h-6" />
                </div>
              </div>

              <div className="bg-neutral-900/60 p-4 rounded-2xl border border-neutral-800/80 space-y-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl font-display font-black text-white">From $2,000</span>
                </div>
                <div className="text-[11px] font-mono text-brand-400 font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 shrink-0" />
                  <span>50% kickoff • 50% upon live launch</span>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Custom, mobile-friendly websites with contact forms, search setup, and 100% client ownership.
              </p>

              <div className="p-3 rounded-xl bg-neutral-900/40 border border-neutral-900 space-y-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold block">
                  Ownership Policy:
                </span>
                <p className="text-xs text-slate-200 font-medium leading-relaxed">
                  You retain 100% direct ownership of domain registrars and hosting accounts.
                </p>
              </div>

              <div className="pt-3 border-t border-neutral-900 space-y-2.5">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold block">
                  Included Services:
                </span>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                    <span>Clean custom design for phones, tablets &amp; desktops</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                    <span>Software installs, CMS &amp; platform configuration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                    <span>Google search optimization (SEO) setup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                    <span>Secure contact forms with instant notifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                    <span>Domain setup &amp; Cloudflare speed enhancements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />
                    <span>Full project backup provided upon launch</span>
                  </li>
                </ul>
              </div>
            </div>

            <button 
              onClick={onNavigateToWebOnboarding} 
              className="mt-6 w-full py-3.5 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider transition-all duration-200 cursor-pointer shadow-md active:scale-95 flex items-center justify-center gap-2"
            >
              <span>Start Website Project</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Billing Transparency & Payment Terms */}
      <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-brand-500/20 text-left space-y-6">
        <div className="space-y-1 border-b border-neutral-900 pb-4">
          <div className="inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-brand-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            Billing Transparency &amp; Payment Terms
          </div>
          <h3 className="text-xl sm:text-2xl font-display font-bold text-white">
            Clear expectations for every client engagement
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-xs text-slate-300 leading-relaxed">
          <div className="p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800/80 space-y-2 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-brand-400" />
                <h4 className="text-sm font-bold text-white font-display">
                  Payment upon Receipt
                </h4>
              </div>
              <p className="text-slate-300 leading-relaxed">
                Payment is due upon receipt of services or delivery of equipment. Cash, checks (payable to <strong className="text-white">Randon Brown, LLC</strong>), and major credit cards processed via <strong className="text-white">Zoho Payments</strong> are accepted. Credit card payments are subject to a processing service charge. No deposit is required for standard technical repairs.
              </p>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800/80 space-y-2 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-brand-400" />
                <h4 className="text-sm font-bold text-white font-display">
                  "No Sticker Shock"
                </h4>
              </div>
              <p className="text-slate-300 leading-relaxed">
                We always provide upfront estimates and only proceed with your authorization. If an issue turns out to be more complex than expected, work pauses and we contact you first before doing any additional work.
              </p>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-neutral-900/60 border border-neutral-800/80 space-y-2 flex flex-col justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-brand-400" />
                <h4 className="text-sm font-bold text-white font-display">
                  100% Client Ownership
                </h4>
              </div>
              <p className="text-slate-300 leading-relaxed">
                For websites and domain setups, you retain 100% direct ownership of your domain registrars and hosting accounts. We are simply delegated as administrators to keep you secure.
              </p>
            </div>
          </div>
        </div>

        {/* Legal Disclosures & Full Terms Button */}
        <div className="pt-4 border-t border-neutral-900/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="text-xs text-slate-400">
            Read complete legal terms, liability limits, and diagnostic disclosures:
          </p>
          {onViewTerms && (
            <button
              onClick={onViewTerms}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-brand-400 hover:text-brand-300 border border-brand-500/20 hover:border-brand-500/40 text-xs font-mono font-bold uppercase tracking-wider transition-all cursor-pointer w-fit shadow-sm active:scale-95"
            >
              <FileText className="w-4 h-4" />
              <span>View Full Terms &amp; Conditions</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
